import { useRef, useState } from 'react';
import api from '../../services/api';
import { newReference } from './merchandisingTypes';
import { readNokeError } from './restockPlan';

interface PendingWrite { method: 'post' | 'put'; path: string; body: Record<string, unknown> }

const paths: Record<string, string> = {
  '/api/noke/merchandising/settings': 'put', '/api/noke/merchandising/plans': 'post',
  '/api/noke/merchandising/placements': 'post', '/api/noke/merchandising/fittings': 'post',
  '/api/noke/merchandising/outlet-transfer': 'post',
  '/api/noke/merchandising/planning': 'put', '/api/noke/merchandising/demand-signals': 'post',
  '/api/noke/inventory/counts': 'post',
};

/** Keeps an uncertain write's exact reference/body across closing a dialog or refreshing the plan. */
export function useMerchandisingWrite(key: string) {
  const storageKey = `noke-merchandising-write:${key}`;
  const [pending, setPending] = useState<PendingWrite | null>(() => {
    try {
      const value = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      return value && paths[value.path] === value.method && typeof value.body?.client_ref === 'string' ? value : null;
    } catch { return null; }
  });
  const pendingRef = useRef(pending);
  const running = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  const persist = (value: PendingWrite | null) => {
    pendingRef.current = value;
    setPending(value);
    try {
      if (value) sessionStorage.setItem(storageKey, JSON.stringify(value));
      else sessionStorage.removeItem(storageKey);
    } catch { /* in-memory reference still protects retries while this dialog is open */ }
  };

  const submit = async (method: 'post' | 'put', path: string, body: Record<string, unknown>): Promise<boolean> => {
    if (running.current) return false;
    const command = pendingRef.current || { method, path, body: { ...body, client_ref: newReference(key) } };
    persist(command);
    running.current = true;
    setBusy(true);
    setError('');
    setErrorStatus(null);
    try {
      await api[command.method](command.path, command.body);
      persist(null);
      return true;
    } catch (err: any) {
      const status = err.response?.status;
      setErrorStatus(typeof status === 'number' ? status : null);
      const definitive = status >= 400 && status < 500 && status !== 408 && status !== 429;
      if (definitive) persist(null);
      setError(`${readNokeError(err, '未能確認是否完成。')}${definitive ? ' 請核對資料後再試。' : ' 請重試同一筆，避免重複登記。'}`);
      return false;
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  return { submit, busy, error, errorStatus, pending, locked: busy || pending !== null };
}
